import uniqBy from 'lodash/unionBy';
import omit from 'lodash/omit';
import { dateUtils } from '@utils';
import categories from './categories';
import genre from './genre';
import { apiService } from '@/api';

const STORE_NAME = 'topSellingEvents';
const OFFSET_DISPLAY_LIMIT = 100;

export default {
    name: STORE_NAME,
    namespaced: true,
    state: {
        eventTitle: '',
        dateRange: {
            start: dateUtils.getDateStartOfThisWeek(),
            end: dateUtils.getDateEndOfThisWeek(),
        },
        loading: false,
        hasSearch: false,
        list: [],
        totalCount: 0,
        offset: 0,
        params: {},
        allEventsLoaded: false,
        selectedEvent: null,
    },
    mutations: {
        setSelectedEvent(state, event) {
            if (state.selectedEvent && state.selectedEvent.id === event.id) {
                state.selectedEvent = null;
            } else {
                state.selectedEvent = event;
            }
        },
        setEventTitle(state, eventTitle) {
            state.eventTitle = eventTitle;
        },
        setDateRange(state, dateRange) {
            state.dateRange = dateRange;
        },
        setList(state, list) {
            state.list = list;
        },
        setListItems(state, listItems) {
            state.list = { ...state.list, ...listItems };
        },
        setLoading(state, loading) {
            state.loading = loading;
        },
        setHasSearch(state, hasSearch) {
            state.hasSearch = hasSearch;
        },
        setParams(state, params) {
            state.params = params;
        },
        setTotalCount(state, totalCount) {
            state.totalCount = totalCount;
        },
        setOffset(state, offset) {
            state.offset = offset;
        },
        setAllEventsLoaded(state, allEventsLoaded) {
            state.allEventsLoaded = allEventsLoaded;
        },
        appendSameDateEvents(state, { date, events }) {
            const newSameDateEvents = [...state.list[date].items, ...events];

            state.list[date].items = uniqBy(newSameDateEvents, 'id');
        },
    },
    actions: {
        setSelectedEvent({ commit }, event) {
            commit('setSelectedEvent', event);
        },
        setEventTitle({ commit }, eventTitle) {
            commit('setEventTitle', eventTitle);
        },
        setDateRange({ commit }, dateRange) {
            commit('setDateRange', dateRange);
        },
        getTopSellingEvents: async ({ commit, state }, params = {}) => {
            commit('setLoading', true);
            commit('setParams', params);

            const newParams = sanitizeEventsApiCallParams(params);
            const response = await apiService.topSellingEvents.events.get(newParams);

            if (response.success) {
                commit('setHasSearch', true);
                commit('setOffset', state.offset + OFFSET_DISPLAY_LIMIT);

                const items = response.data.items;
                const finalItems = sanitizeEvents(items);

                params.shouldAppendListItems
                    ? commit('setListItems', finalItems)
                    : commit('setList', finalItems);

                commit('setTotalCount', response.data.items.length);
                commit('setAllEventsLoaded', response.data.items.length < newParams.limit);

                await getSameDateEvents(
                    commit,
                    state,
                    items,
                    response,
                    omit(newParams, 'sampleSize')
                );

                commit('setHasSearch', true);
                commit('setLoading', false);
                return finalItems;
            }

            commit('setHasSearch', true);
            commit('setLoading', false);
            return [];
        },
        setLoading({ commit }, loading) {
            commit('setLoading', loading);
        },
    },
    modules: {
        categories,
        genre,
    },
};

function sanitizeEventsApiCallParams(params) {
    const newParams = {
        eventDateFrom: params.startDate,
        eventDateTo: params.endDate,
        hotEvents: true,
        ticketsAvailable: true,
        limit: 100,
        sampleSize: 12,
        offset: params.offset,
    };

    if (params.countryCode && params.countryCode.length > 0) {
        newParams.countryCode = params.countryCode;
    }

    if (params.stateCode && params.stateCode.length > 0) {
        newParams.stateCode = params.stateCode;
    }

    if (params.cityName && params.cityName.length > 0) {
        newParams.cityName = params.cityName;
    }

    if (params.categories && params.categories.length > 0) {
        newParams.segmentIds = params.categories.map((category) => category.value);
    }

    if (params.genre && params.genre.length > 0) {
        newParams.genreNames = params.genre.map((genre) => genre.value);
    }

    if (params.attractions && params.attractions.length > 0) {
        newParams.attractionIds = params.attractions.map((attraction) => attraction.value);
    }

    if (params.keyword && params.keyword.length > 0) {
        newParams.eventName = params.keyword;
    }

    return newParams;
}

function sanitizeEvents(items) {
    const finalItems = items.reduce((acc, curr) => {
        return {
            ...acc,
            [curr.date]: {
                ...acc[curr.date],
                items: curr.events,
                totalCount: curr.totalCount,
            },
        };
    }, {});

    return finalItems;
}

async function getSameDateEvents(commit, state, items, response, params) {
    if (!items.length) {
        return;
    }

    items.forEach(async (item) => {
        let offset = 0;
        const stateItem = state.list[item.date];

        if (stateItem) {
            while (stateItem.items.length < stateItem.totalCount) {
                const res = await apiService.topSellingEvents.sameDateEvents.get({
                    ...params,
                    eventDateFrom: item.date,
                    eventDateTo: item.date,
                    hotEvents: true,
                    ticketsAvailable: true,
                    limit: 100,
                    offset,
                });

                if (response.success) {
                    commit('appendSameDateEvents', res.data.items[0]);
                }

                if (state.list[item.date].items.length <= state.list[item.date].totalCount) {
                    offset += OFFSET_DISPLAY_LIMIT;
                }
            }
        }
    });
}
